/// <amd-module name="Test/MetadataHandler/Sources/FilterMetadataSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { TestReportSource } from "Test/Analytics/Common/Sources/TestReportSource";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { areReleaseEntitiesRemoved } from "Test/Analytics/Common/Utils/FeatureFlagUtils";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { FilterValueItem } from "Test/common-util/TestResults.Filtering.Common";
import * as RMContracts from "Test/Release/Client/Contracts/Release";
import { IReleaseRestClient } from "Test/Release/Client/RestClient/Release";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssContributionService } from "VSS/Platform/Contribution";

export class FilterMetadataSource extends TestReportSource {
    public async queryReleaseDefinition(releaseDefinitionId: number): Promise<RMContracts.ReleaseDefinition> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            const projectName: string = tfsPageData.project.name;
            const releaseClient: IReleaseRestClient = await this._getReleaseClient();
            return releaseClient.getReleaseDefinition(projectName, releaseDefinitionId);
        } else {
            throw new Error("Project data not available");
        }
    }

    public async queryReleaseDefinitionsByBuildArtifact(buildDefinitionId: number): Promise<RMContracts.ReleaseDefinition[]> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            const projectName: string = tfsPageData.project.name;
            const artifactSourceId = `${tfsPageData.project.id}:${buildDefinitionId}`;
            const releaseClient: IReleaseRestClient = await this._getReleaseClient();
            return releaseClient.getReleaseDefinitions(
                projectName,
                undefined,
                RMContracts.ReleaseDefinitionExpands.Environments,
                "Build",
                artifactSourceId
            );
        } else {
            throw new Error("Project data not available");
        }
    }

    public queryFilterFieldValues(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration,
        filterBy: CommonTypes.Filter
    ): Promise<TCMContracts.FieldDetailsForTestResults> {
        let entityType: string;
        let groupByStr: string;
        let filterStr: string;

        switch (filterBy) {
            case CommonTypes.Filter.Container:
            case CommonTypes.Filter.Owner:
                groupByStr = this._getGroupByStr(filterBy);
                entityType = this._testResultDailyEntityName;
                filterStr = `filter(${this._getTestResultsContextPipelineFilter(testReportContext)} and ${this._getDailyDateFilter(
                    confValues.trendBy,
                    confValues.period
                )})`;
                break;
            case CommonTypes.Filter.TestRun:
            case CommonTypes.Filter.Branch:
            case CommonTypes.Filter.Environment:
                entityType = this._testRunEntityName;
                filterStr = ``;
                groupByStr = this._getGroupByStr(filterBy);
                break;
            default:
                groupByStr = this._getGroupByStr(filterBy);
                entityType = this._testResultDailyEntityName;
                filterStr = `filter(${this._getTestResultsContextPipelineFilter(testReportContext)} and ${this._getDailyDateFilter(
                    confValues.trendBy,
                    confValues.period
                )})`;
        }

        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");

        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;

            const queryOptions = {
                entityType: entityType,
                project: projectId
            } as ODataQueryOptions;

            if (!filterStr) {
                filterStr = `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                    confValues.trendBy,
                    confValues.period
                )})`;
            }

            queryOptions.$apply = `${filterStr}/groupby((${groupByStr}))`;

            return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
                if (!data || !data.value) {
                    throw new Error("Data is empty");
                }

                return this._getFilterFieldValuesFromRawData(filterBy, data);
            });
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _getFilterFieldValuesFromRawData(
        filterBy: CommonTypes.Filter,
        data: CommonTypes.IODataQueryResponse
    ): TCMContracts.FieldDetailsForTestResults {
        let filterName = "";
        const groupsForField: FilterValueItem[] = [];

        (data.value as any[]).forEach(d => {
            let itemKey = null,
                itemValue = null;

            switch (filterBy) {
                case CommonTypes.Filter.TestRun:
                    filterName = CommonTypes.Filter.TestRun;
                    itemKey = itemValue = d.Title;
                    break;
                case CommonTypes.Filter.Container:
                    filterName = CommonTypes.Filter.Container;
                    itemKey = itemValue = d.Test.ContainerName;
                    break;
                case CommonTypes.Filter.Owner:
                    filterName = CommonTypes.Filter.Owner;
                    itemKey = itemValue = d.Test.TestOwner;
                    break;
                case CommonTypes.Filter.Branch:
                    filterName = CommonTypes.Filter.Branch;
                    itemKey = d.BranchSK;
                    itemValue = d.Branch.BranchName;
                    break;
                case CommonTypes.Filter.Environment:
                    filterName = CommonTypes.Filter.Environment;
                    if (areReleaseEntitiesRemoved(this.pageContext)) {
                        itemKey = d.ReleaseStageId;
                        itemValue = d.ReleaseStageId;
                    } else {
                        itemKey = d.ReleaseStageSK;
                        itemValue = d.ReleaseStage.ReleaseStageId;
                    }
                    break;
                default:
                    filterName = CommonTypes.Filter.TestRun;
                    itemKey = itemValue = d.Title;
                    break;
            }

            if (itemValue || itemValue === this._emptyString) {
                groupsForField.push(new FilterValueItem(itemKey as string, itemValue as string));
            }
        });

        return { fieldName: filterName, groupsForField: groupsForField } as TCMContracts.FieldDetailsForTestResults;
    }

    private _getGroupByStr(filter: CommonTypes.Filter): string {
        switch (filter) {
            case CommonTypes.Filter.Container:
                return this._getGroupByTestPropertiesString(CommonTypes.GroupBy.Container);
            case CommonTypes.Filter.Owner:
                return this._getGroupByTestPropertiesString(CommonTypes.GroupBy.Owner);
            case CommonTypes.Filter.Branch:
                return "BranchSK, Branch/BranchName";
            case CommonTypes.Filter.Environment:
                return areReleaseEntitiesRemoved(this.pageContext) ? "ReleaseStageId" : "ReleaseStageSK, ReleaseStage/ReleaseStageId";
            case CommonTypes.Filter.TestRun:
                return this._getGroupByTestRunPropertiesString(CommonTypes.GroupBy.TestRun);
            default:
                return this._getGroupByTestRunPropertiesString(CommonTypes.GroupBy.TestRun);
        }
    }

    private async _getReleaseClient(): Promise<IReleaseRestClient> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const ReleaseClientName = "Release_4.1.IReleaseRestClient";
        await contributionService.getContributionAsync("ms.vss-test-web.release-client");
        return this.pageContext.getRestClient<IReleaseRestClient>(ReleaseClientName);
    }
}
